import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Stack, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { parseCoordinates } from '../../utils/geoUtils';

// Helper to create GeoJSON features with unique IDs
const createFeaturesFromCoords = (type, coordinates) => {
  if (!coordinates || coordinates.length === 0) return [];

  if (type === 'MultiPoint') {
    return coordinates.map(coord => ({
      type: 'Feature',
      id: Date.now() + Math.random(),
      geometry: { type: 'Point', coordinates: coord },
      properties: {},
    }));
  }

  let geometry = { type, coordinates };
  if (type === 'Polygon') {
    if (coordinates.length < 3) throw new Error("A polygon requires at least 3 coordinates.");
    if (JSON.stringify(coordinates[0]) !== JSON.stringify(coordinates[coordinates.length - 1])) {
      coordinates.push(coordinates[0]);
    }
    geometry.coordinates = [coordinates];
  } else if (type === 'LineString' && coordinates.length < 2) {
    throw new Error("A LineString requires at least 2 coordinates.");
  }

  return [{
    type: 'Feature',
    id: Date.now() + Math.random(),
    geometry,
    properties: {},
  }];
};

function CoordinateInputPanel({ onGeoJsonChange, currentGeoJson }) {
  const [rawCoords, setRawCoords] = useState('40.7128, -74.0060\n40.7580, -73.9855\n40.7831, -73.9712');
  const [geometryType, setGeometryType] = useState('LineString');

  const handleAdd = () => {
    try {
      const parsedCoords = parseCoordinates(rawCoords);
      if (parsedCoords.length === 0) throw new Error("No valid coordinates found.");

      const newFeatures = createFeaturesFromCoords(geometryType, parsedCoords);
      
      // This now works correctly because the parent's `handleGeoJsonChange` is no longer stale.
      onGeoJsonChange({
        ...currentGeoJson,
        features: [...currentGeoJson.features, ...newFeatures],
      });
      setRawCoords('');
    } catch (error) {
      alert(error.message);
    }
  };

  const handleRemoveFeature = (featureId) => {
    onGeoJsonChange({
      ...currentGeoJson,
      features: currentGeoJson.features.filter(f => f.id !== featureId),
    });
  };

  const handleClearAll = () => {
    onGeoJsonChange({ type: 'FeatureCollection', features: [] });
  };

  const getFeatureDescription = (feature) => {
    const type = feature.geometry.type;
    const coords = feature.geometry.coordinates;
    if (type === 'Point') return `Point (${coords[1].toFixed(2)}, ${coords[0].toFixed(2)})`;
    if (type === 'LineString') return `LineString (${coords.length} vertices)`;
    if (type === 'Polygon') return `Polygon (${coords[0].length - 1} vertices)`;
    return type;
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Add Features from Coordinates</Typography>
      <TextField
        label="Coordinates (Lat, Lon per line)"
        value={rawCoords}
        onChange={(e) => setRawCoords(e.target.value)}
        fullWidth
        multiline
        rows={5}
        variant="outlined"
      />
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Feature Type</InputLabel>
          <Select value={geometryType} label="Feature Type" onChange={(e) => setGeometryType(e.target.value)}>
            <MenuItem value="LineString">LineString</MenuItem>
            <MenuItem value="Polygon">Polygon</MenuItem>
            <MenuItem value="MultiPoint">Points</MenuItem>
          </Select>
        </FormControl>
        <Button onClick={handleAdd} variant="contained" startIcon={<AddIcon />}>
          Add
        </Button>
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3, mb: 1 }}>
        <Typography variant="h6">Features on Map</Typography>
        <Button onClick={handleClearAll} color="error" size="small" disabled={currentGeoJson.features.length === 0}>
          Clear All
        </Button>
      </Stack>
      
      <Stack spacing={1} sx={{ maxHeight: '30vh', overflowY: 'auto', pr: 1 }}>
        {currentGeoJson.features.map(feature => (
          <Stack key={feature.id} direction="row" alignItems="center" sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography sx={{ flexGrow: 1, fontFamily: 'monospace', fontSize: '0.9rem' }}>
              {getFeatureDescription(feature)}
            </Typography>
            <Tooltip title="Remove">
              <IconButton onClick={() => handleRemoveFeature(feature.id)} size="small">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

export default CoordinateInputPanel;
